import React, { useCallback, useEffect, useState } from 'react';

import { get } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import RestaurantRedemptionModal from 'components/offer-redemption-modal/redemption-modal';
import { PromotionType, useCreateOrderlessTransactionMutation } from 'generated/graphql-gateway';
import { OfferType } from 'generated/rbi-graphql';
import { useFeatureEarningCalculationQuery } from 'generated/sanity-graphql';
import useDialogModal from 'hooks/use-dialog-modal';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { useSwitchCartMethodDialog } from 'hooks/use-switch-cart-method-dialog';
import { OFFERS } from 'pages/loyalty/loyalty-constants';
import { parseIncentiveData } from 'pages/loyalty/loyalty-incentives-components/incentive-card/parse-incentive-data';
import {
  getMenuItemDescriptorForIncentive,
  makeUrlForIncentive,
} from 'pages/loyalty/loyalty-incentives-components/incentive-details/utils';
import { ModalRewardItemUnavailable } from 'pages/loyalty/loyalty-incentives-components/modal-reward-item-unavailable-modal';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { useLogger } from 'state/logger';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyRewards } from 'state/loyalty/hooks/use-loyalty-rewards';
import { useInRestaurantRedemptionContext } from 'state/loyalty/in-restaurant-redemption';
import { ICreateCartEntryParams } from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart';
import { canStackReward } from 'state/loyalty/in-restaurant-redemption/hooks/use-in-restaurant-redemption-cart/utils';
import {
  LoyaltyAppliedOffer,
  isLoyaltyOffer,
  isReward,
  isSurpriseOffer,
} from 'state/loyalty/types';
import {
  getCanStackOfferCheck,
  isAppliedOfferSwap,
  isDiscountLoyaltyOffer,
} from 'state/loyalty/utils';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { DIFFERENCE_TIME_UNITS, getDifferenceToNow } from 'utils/dateTime';
import { routes } from 'utils/routing';
import { getFirstStringInLocaleBlockContent } from 'utils/sanity';
import { toast } from 'utils/toast';

import { AddToCartArgs, Incentive, RedemptionMethod } from './types';

/**
 * Wraps the param Component in another component in order to add the function
 * addIncentiveToCart which takes an incentive and a redemption type and adds it to the loyaltyContext
 * It also handles and shows the related modals for each situation.
 * Eg: Shows "only one coupon allowed" if you already have an Offer in the cart.
 *
 * @param Component The component you want to wrap with this logic
 */
const withAddIncentiveToCart = (Component: React.FC<any>) => {
  return (props: any) => {
    // Utils
    const logger = useLogger();
    const { formatMessage } = useIntl();
    const { navigate, location, setStoreLocatorCallbackUrl } = useLocationContext();
    const enableStaticMenu = useFlag(LaunchDarklyFlag.ENABLE_STATIC_MENU);
    const enableCooldownToast = useFlag(LaunchDarklyFlag.ENABLE_COOLDOWN_TOAST_INFO);
    const enableAlwaysShowRedeemInStoreButtonForRewards = useFlag(
      LaunchDarklyFlag.ENABLE_ALWAYS_SHOW_REDEEM_INSTORE_BUTTON_FOR_REWARDS
    );
    const isMobile = useMediaQuery(MediaQuery.Mobile);
    const { logRBIEvent } = useMParticleContext();
    const { pathname } = useLocation();
    const isOffersPage = pathname.includes(routes.offers);

    // State
    const [incentiveName, setIncentiveName] = useState<string>('');
    const [selectedIncentive, setSelectedIncentive] = useState<Incentive>();
    const [showOfferRedemptionModal, setShowOfferRedemptionModal] = useState(false);
    const [rewardLimitePerOrder, setRewardLimitePerOrder] = useState<number | undefined | null>(0);

    // Context
    const { engineRewardsMap } = useLoyaltyRewardsList();
    const { noStoreSelected } = useStoreContext();
    const { setSelectedStaticMenuItem } = useMenuContext();
    const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
    const dispatch = useAppDispatch();
    const { serviceMode } = useServiceModeContext();
    const { removeFromCart } = useOrderContext();
    const { refetchLoyaltyUser } = useLoyaltyContext();
    const appliedLoyaltyRewardsArray = useAppSelector(
      selectors.loyalty.selectAppliedLoyaltyRewardsArray
    );

    const {
      inRestaurantRedemptionCart,
      inRestaurantLoyaltyEnabledAtRestaurant,
      inRestaurantRedemptionEnabled,
      addInRestaurantRedemptionEntry,
      enableOfferRedemption,
      shouldForceRestaurantSelection,
      clearInRestaurantRedemptionAllRewards,
    } = useInRestaurantRedemptionContext();
    const loyaltyUser = useAppSelector(selectors.loyalty.selectUser);
    const [InvalidIncentiveModal, openInvalidIncentiveModal] = useDialogModal({
      modalAppearanceEventMessage: 'Invalid In Restaurant Incentive Redemption',
    });
    const [createOrderlessTransaction] = useCreateOrderlessTransactionMutation({
      variables: {
        input: {
          loyaltyId: loyaltyUser?.id,
          channel: 'ORDERLESS',
          incentives: [{ id: selectedIncentive?.loyaltyEngineId || '', type: PromotionType.OFFER }],
        },
      },
      onCompleted: () => {
        refetchLoyaltyUser();
        setShowOfferRedemptionModal(true);
      },
      onError: () =>
        openInvalidIncentiveModal({ message: formatMessage({ id: 'unableToPlaceOrder' }) }),
    });

    const { data: earningCalculationData } = useFeatureEarningCalculationQuery({
      variables: { id: 'earningCalculation' },
    });
    const standardOffersLimit =
      earningCalculationData?.EarningCalculation?.offerRedemptionLimits?.standardOffersLimit || 1;

    const { isLimitRewardRedemptionPerOrderReached } = useLoyaltyRewards(loyaltyUser);

    const diffInMin = getDifferenceToNow(
      DIFFERENCE_TIME_UNITS.MINUTES,
      loyaltyUser?.offerRedemptionAvailability?.availableAfter || ''
    );

    const showOfferCooldownToast = () => {
      //If the diffInMin is greater than 0 it means that the user already have an offer in course so we do nothing.
      if (!enableCooldownToast || diffInMin > 0 || !serviceMode) {
        return;
      }

      toast.default(
        formatMessage(
          { id: 'offersCooldownInfo' },
          {
            period: earningCalculationData?.EarningCalculation?.offerRedemptionCooldownPeriod,
          }
        )
      );
    };

    // Dialogs
    const [ReplaceOfferDialog, openReplaceOfferDialog] = useDialogModal({
      modalAppearanceEventMessage: 'Confirm Replace Coupon',
      onConfirm: (data: { args: AddToCartArgs }) => redeemIncentive(data.args),
    });
    const [ReplaceRewardDialog, openReplaceRewardDialog] = useDialogModal({
      modalAppearanceEventMessage: 'Confirm Replace Reward',
      onConfirm: (data: { args: AddToCartArgs; incentiveMenuPath?: string }) =>
        onConfirmReplaceReward(data.args, data.incentiveMenuPath),
    });
    const [ItemUnavailableModal, openItemUnavailableModal] = useDialogModal<
      {},
      { rewardName: string }
    >({
      Component: ModalRewardItemUnavailable,
    });
    const [ReadyToRedeemDialog, openReadyToRedeemDialog] = useDialogModal({
      modalAppearanceEventMessage: 'Confirm Ready to Redeem',
      onConfirm: () => {
        if (selectedIncentive && isLoyaltyOffer(selectedIncentive)) {
          const name = selectedIncentive.name;
          logRBIEvent({
            name: 'Legacy Offer Added to In Store Order',
            type: EventTypes.Other,
            attributes: {
              sanityId: selectedIncentive._id || '',
              name: name ? getFirstStringInLocaleBlockContent(name) : '',
            },
          });
          logRBIEvent({
            name: CustomEventNames.OFFER_ADDED_TO_ORDER,
            type: EventTypes.Other,
            attributes: {
              engineId: selectedIncentive?.loyaltyEngineId ?? '',
              sanityId: selectedIncentive?._id ?? '',
              name: name ? getFirstStringInLocaleBlockContent(name) : '',
              redemptionMode: 'In-Store',
            },
          });
        }

        if (loyaltyUser?.id && selectedIncentive?.loyaltyEngineId) {
          createOrderlessTransaction();
        } else {
          setShowOfferRedemptionModal(true);
        }
        if (isOffersPage) {
          showOfferCooldownToast();
        }
      },
      showCancel: false,
    });
    const {
      SwitchCartMethodDialog,
      shouldShowSwitchToMobileDialog,
      shouldShowSwitchToStoreDialog,
      openSwitchCartMethod,
    } = useSwitchCartMethodDialog();

    // Handle add to cart flow
    const addIncentiveToCart = (args: AddToCartArgs) => {
      setSelectedIncentive(args.incentive);
      if (isLoyaltyOffer(args.incentive)) {
        const canStackOfferToMobile = getCanStackOfferCheck((of: LoyaltyAppliedOffer) => of);
        const canStackOfferToInRestaurant = getCanStackOfferCheck(entry =>
          get(entry, 'details.offer')
        );

        const showReplaceModal =
          !canStackOfferToMobile(args.incentive, appliedOffers, standardOffersLimit) ||
          !canStackOfferToInRestaurant(
            args.incentive,
            inRestaurantRedemptionCart,
            standardOffersLimit
          );

        if (showReplaceModal) {
          return openReplaceOfferDialog({ args });
        }
      }

      redeemIncentive(args);
    };

    const redeemIncentive = (args: AddToCartArgs) => {
      if (args.method === RedemptionMethod.MOBILE) {
        return handleMobileRedemption(args);
      }
      tryInRestaurantRedemption(args);
    };

    const tryInRestaurantRedemption = (args: AddToCartArgs) => {
      if (shouldShowSwitchToStoreDialog) {
        openSwitchCartMethod({
          onConfirm: () => handleInRestaurantRedemption(args),
        });
      } else {
        handleInRestaurantRedemption(args);
      }
    };

    const handleMobileRedemption = (args: AddToCartArgs) => {
      if (shouldShowSwitchToMobileDialog) {
        openSwitchCartMethod({ onConfirm: () => mobileRedemption(args) });
      } else {
        mobileRedemption(args);
      }
    };

    const handleInRestaurantRedemption = (args: AddToCartArgs) => {
      const isInStoreLoyaltyRedemptionEnabled =
        isMobile && inRestaurantRedemptionEnabled && inRestaurantLoyaltyEnabledAtRestaurant;
      const shouldRedeemInRestaurant =
        enableAlwaysShowRedeemInStoreButtonForRewards ||
        (isInStoreLoyaltyRedemptionEnabled && (isReward(args.incentive) || enableOfferRedemption));

      if (shouldRedeemInRestaurant) {
        inRestaurantRedemption(args);
      } else {
        openReadyToRedeemDialog();
      }
    };

    const createInRestaurantCartEntry = useCallback(
      (incentive: AddToCartArgs['incentive']) => {
        let inRestaurantCartEntry: ICreateCartEntryParams | undefined;
        if (!isReward(incentive)) {
          inRestaurantCartEntry = { offer: incentive };
        } else {
          const engineReward = engineRewardsMap[incentive?.loyaltyEngineId || ''];
          if (engineReward) {
            inRestaurantCartEntry = {
              reward: incentive,
              engineReward,
            };
          }
        }
        return inRestaurantCartEntry;
      },
      [engineRewardsMap]
    );

    const mobileRedemption = (args: AddToCartArgs) => {
      const { incentive } = args;
      if (isLoyaltyOffer(incentive)) {
        dispatch(actions.loyalty.setSelectedOffer(incentive));

        if (isDiscountLoyaltyOffer(incentive)) {
          if (!incentive.isStackable) {
            appliedOffers.forEach(offer => {
              // removes offer from cart if the applied offer is not stackable or a swap offer
              if (!offer.isStackable && !isAppliedOfferSwap(offer)) {
                removeFromCart({ cartId: offer.cartId });
              }
            });
          }

          // Discount offers should not show menu item details
          dispatch(
            actions.loyalty.applyOffer({
              id: incentive.loyaltyEngineId,
              type: OfferType.GLOBAL,
              isStackable: incentive.isStackable,
              isSurprise: isSurpriseOffer(incentive),
              cmsId: incentive._id,
              cartId: 'discount-offer',
            })
          );
          toast.success(formatMessage({ id: 'offerAddedToCart' }));
          const name = incentive.name;
          logRBIEvent({
            name: CustomEventNames.OFFER_ADDED_TO_ORDER,
            type: EventTypes.Other,
            attributes: {
              engineId: incentive?.loyaltyEngineId ?? '',
              sanityId: incentive?._id ?? '',
              name: name ? getFirstStringInLocaleBlockContent(name) : '',
              redemptionMode: 'Online',
            },
          });
          navigate('/menu');
          return;
        }

        if (isOffersPage) {
          showOfferCooldownToast();
        }
      }

      const engineReward = engineRewardsMap[incentive?.loyaltyEngineId || ''];
      const menuItemDescriptor = getMenuItemDescriptorForIncentive(
        incentive,
        engineReward?.rewardBenefitId
      );
      const incentiveMenuPath = makeUrlForIncentive(incentive, menuItemDescriptor);
      if (!menuItemDescriptor || !incentiveMenuPath) {
        logger.warn(
          `Attempting to make a url for reward that is missing data: reward ${incentive._id}`
        );
        const incentiveData = parseIncentiveData(incentive);
        setIncentiveName(incentiveData.name);
        openItemUnavailableModal();
        return;
      }

      // Save incentive data before redirecting to StoreLocator
      if (noStoreSelected && !enableStaticMenu) {
        const searchIndex = incentiveMenuPath.indexOf('?');
        const search = searchIndex !== -1 ? incentiveMenuPath.slice(searchIndex) : '';
        setSelectedStaticMenuItem({
          itemId: `${menuItemDescriptor._id}-${menuItemDescriptor._type}`,
          itemName: incentiveName || '',
          search,
        });
      }

      if (isLimitRewardRedemptionPerOrderReached) {
        return openReplaceRewardDialog({ args, incentiveMenuPath });
      }

      navigate(incentiveMenuPath, { state: { isFromSurprise: location.pathname === routes.cart } });
    };

    const inRestaurantRedemption = (args: AddToCartArgs) => {
      const { incentive } = args;
      const engineReward = engineRewardsMap[incentive?.loyaltyEngineId || ''];
      const shouldSelectStore = shouldForceRestaurantSelection && noStoreSelected;
      if (shouldSelectStore) {
        setStoreLocatorCallbackUrl(location.pathname);
        navigate(formatMessage({ id: 'routes.storeLocator' }));
      } else if (!isReward(incentive)) {
        addInRestaurantRedemptionEntry({ offer: incentive });
      } else if (engineReward) {
        if (!canStackReward(inRestaurantRedemptionCart, rewardLimitePerOrder)) {
          return openReplaceRewardDialog({ args });
        }
        addInRestaurantRedemptionEntry({
          reward: incentive,
          engineReward,
        });
      }
      if (isOffersPage) {
        showOfferCooldownToast();
      }
    };

    const onConfirmReplaceReward = useCallback(
      (args: AddToCartArgs, incentiveMenuPath?: string) => {
        const { incentive, method } = args;

        if (method === RedemptionMethod.IN_RESTAURANT) {
          const inRestaurantCartEntry = createInRestaurantCartEntry(incentive);
          if (inRestaurantCartEntry) {
            clearInRestaurantRedemptionAllRewards();
            addInRestaurantRedemptionEntry(inRestaurantCartEntry);

            return navigate(routes.redeem, { replace: true });
          }
        }

        // get the first item to replace, in case more than one is available
        const itemToReplace = appliedLoyaltyRewardsArray && appliedLoyaltyRewardsArray[0];

        if (itemToReplace) {
          dispatch(
            actions.loyalty.removeAppliedReward({
              rewardBenefitId: itemToReplace.rewardId,
              cartId: itemToReplace.cartId,
            })
          );

          removeFromCart({ cartId: itemToReplace.cartId });
        }

        if (incentiveMenuPath) {
          navigate(incentiveMenuPath, {
            state: { isFromSurprise: location.pathname === routes.cart },
          });
        } else {
          navigate(routes.redeem, { replace: true });
        }
      },
      [
        addInRestaurantRedemptionEntry,
        dispatch,
        navigate,
        location.pathname,
        removeFromCart,
        appliedLoyaltyRewardsArray,
        createInRestaurantCartEntry,
        clearInRestaurantRedemptionAllRewards,
      ]
    );

    const onDismissRestaurantRedemptionModal = () => {
      setShowOfferRedemptionModal(false);
      navigate(`${formatMessage({ id: 'routes.loyaltyHome' })}/${OFFERS}`);
    };

    useEffect(() => {
      setRewardLimitePerOrder(
        earningCalculationData?.EarningCalculation?.rewardsRedemptionConfig?.standardRewardsLimit
      );
    }, [earningCalculationData]);

    return (
      <>
        <ReplaceOfferDialog
          heading={formatMessage({ id: 'offersLimitReached' }, { limit: standardOffersLimit })}
          body={formatMessage({ id: 'replaceOffer' })}
          buttonLabel={formatMessage({ id: 'replace' })}
          showCloseButton
        />
        <ReplaceRewardDialog
          heading={formatMessage({ id: 'replaceReward' }, { limit: rewardLimitePerOrder })}
          body={formatMessage({ id: 'replaceRewardText' })}
          buttonLabel={formatMessage({ id: 'replaceItem' })}
          showCloseButton
        />
        <SwitchCartMethodDialog />
        <ItemUnavailableModal rewardName={incentiveName} />
        {selectedIncentive && isLoyaltyOffer(selectedIncentive) && (
          <>
            <InvalidIncentiveModal heading={formatMessage({ id: 'uhoh' })} showCloseButton />
            <ReadyToRedeemDialog
              data-testid="ready-to-redeem-dialog"
              heading={formatMessage({ id: 'couponWillExpire' })}
              body={formatMessage({ id: 'confirmDialogBody' })}
              buttonLabel={formatMessage({ id: 'redeem' })}
              showCloseButton
            />
            {showOfferRedemptionModal && (
              <RestaurantRedemptionModal
                selectedOffer={selectedIncentive}
                redemptionStartTime={Date.now()}
                onDismiss={onDismissRestaurantRedemptionModal}
              />
            )}
          </>
        )}

        <Component {...props} addIncentiveToCart={addIncentiveToCart} />
      </>
    );
  };
};

export default withAddIncentiveToCart;
